import * as _ from 'lodash';
import {
    AfterViewInit,
    Component,
    ContentChild,
    EventEmitter,
    HostListener,
    Input,
    Output,
    TemplateRef
} from '@angular/core';

import { FcpButtonColors, FcpButtonSizes, FcpButtonVariants } from '../fcp-button';
import { MenuColor, MenuStyle, SplitButtonMenuListItem } from '../fcp-button-split';
import { ConnectedPosition } from '@angular/cdk/overlay';

@Component({
    selector: 'fcp-summary-bar',
    templateUrl: './fcp-summary-bar.component.html',
    styleUrls: ['./fcp-summary-bar.component.less'],
    standalone: false
})
export class FcpSummaryBarComponent implements AfterViewInit {
    @Input() title: string;
    @Input() hideSummary = false;

    @Input() set summary(summaryItems: SummaryItems) {
        this.summaryItems = _.compact([
            summaryItems.first,
            summaryItems.second,
            summaryItems.third,
            summaryItems.fourth,
            summaryItems.fifth
        ]);

        this.enabled = this.isEnabled(this.summaryItems);
    }

    @Input() set button(data: SummaryButton | null) {
        if (!data) {
            this.mainButton = null;
            return;
        }
        const { label, enabled = true, color = FcpButtonColors.Default, size = FcpButtonSizes.Large } = data;
        this.mainButton = {
            enabled,
            label,
            color,
            size
        };
    }

    @Input() set splitButton(data: SummarySplitButton | null) {
        if (!data) {
            this.splitMenuButton = null;
            return;
        }
        const {
            label,
            items,
            enabled = true,
            color = FcpButtonColors.Default,
            size = FcpButtonSizes.Large,
            variant = FcpButtonVariants.Filled,
            menuColor = MenuColor.Subtle,
            menuStyle = MenuStyle.Fluid
        } = data;
        this.splitMenuButton = {
            enabled,
            label,
            color,
            size,
            variant,
            menuColor,
            menuStyle,
            items
        };
    }

    @Input() set secondaryButton(data: SummaryButton | null) {
        if (!data) {
            this.additionalButton = null;
            return;
        }
        const { label, enabled = true, color = FcpButtonColors.Subtle, size = FcpButtonSizes.Large, icon } = data;
        this.additionalButton = {
            enabled,
            label,
            color,
            icon,
            size
        };
    }

    @Input() set secondSecondaryButton(data: SummaryButton) {
        if (!data) {
            this.secondAdditionalButton = null;
            return;
        }
        const { label, enabled = true, color = FcpButtonColors.Subtle, size = FcpButtonSizes.Large, icon } = data;
        this.secondAdditionalButton = {
            enabled,
            label,
            color,
            icon,
            size
        };
    }

    @Output() onClick = new EventEmitter<void>();
    @Output() onSecondaryClick = new EventEmitter<void>();
    @Output() onSecondSecondaryClick = new EventEmitter<void>();
    @Output() onSplitMenuClick = new EventEmitter<void>();

    @ContentChild('itemSuffix') itemSuffixTemplate: TemplateRef<{ item: SummaryItem; index: number }>;

    mainButton?: SummaryButton;
    additionalButton?: SummaryButton;
    secondAdditionalButton: SummaryButton;
    splitMenuButton: SummarySplitButton;
    summaryItems: SummaryItem[];
    enabled = false;
    menuPosition: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
            offsetY: -8
        }
    ];

    public displayMode: DisplayMode = DisplayMode.Desktop;

    public get DisplayMode() {
        return DisplayMode;
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: { target: { innerWidth: number } }) {
        this.setDisplayMode(event.target.innerWidth);
    }

    ngAfterViewInit() {
        this.setDisplayMode(window.innerWidth);
    }

    private setDisplayMode(width: number) {
        if (width < 1280) this.displayMode = DisplayMode.Mobile;
        else this.displayMode = DisplayMode.Desktop;
    }

    private isEnabled(items: SummaryItem[]) {
        return !_.isEmpty(items);
    }
}

export interface SummaryItem {
    title: string;
    value?: string | number;
}

export interface SummaryItems {
    first: SummaryItem;
    second?: SummaryItem;
    third?: SummaryItem;
    fourth?: SummaryItem;
    fifth?: SummaryItem;
}

export interface SummaryButton {
    label?: string;
    enabled: boolean;
    color?: FcpButtonColors;
    icon?: string | string[];
    size?: FcpButtonSizes;
    action?: () => void;
}

export interface SummarySplitButton {
    label: string;
    variant?: FcpButtonVariants;
    color?: FcpButtonColors;
    enabled?: boolean;
    size?: FcpButtonSizes;
    menuColor?: MenuColor;
    menuStyle?: MenuStyle;
    items: SplitButtonMenuListItem[];
    action?: () => void;
}

enum DisplayMode {
    Desktop = 'desktop',
    Mobile = 'mobile'
}
